import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Hammer, RefreshCw, Copy, CheckCircle, XCircle, MinusCircle, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/seo/rankings`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

const ICON = {
  applied: <CheckCircle className="w-4 h-4 text-green-600" />,
  sent_to_build: <Send className="w-4 h-4 text-[#A7468A]" />,
  failed: <XCircle className="w-4 h-4 text-rose-600" />,
};

const JobResult = ({ job }) => (
  <div className="mt-4 space-y-3 text-sm" data-testid="fix-queue-result">
    <p className="font-semibold text-gray-900">{job.status === 'done' ? 'Queue finished' : `Working… ${job.progress}/${job.total}`}</p>
    <ul className="space-y-1.5">
      {job.results.map((r) => (
        <li key={r.query + r.page} className="flex items-start gap-2" data-testid="fix-queue-result-item">{ICON[r.status]}<span><b>{r.query}</b> — {r.detail}</span></li>
      ))}
      {job.skipped.map((r) => (
        <li key={'s' + r.query + r.page} className="flex items-start gap-2 text-gray-600" data-testid="fix-queue-skipped-item"><MinusCircle className="w-4 h-4 text-amber-600" /><span><b>{r.query}</b> skipped: {r.reason}</span></li>
      ))}
    </ul>
    {job.status === 'done' && job.brief && (
      <div className="rounded-lg border border-[#A7468A]/20 bg-[#A7468A]/5 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-semibold text-gray-900">Build brief for main site pages {job.brief_emailed && <span className="text-xs font-normal text-gray-600">(also emailed to jo@audioandco.com)</span>}</p>
          <button onClick={() => { navigator.clipboard.writeText(job.brief); toast.success('Brief copied'); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1E2A44] text-white text-xs font-semibold" data-testid="fix-queue-copy-brief"><Copy className="w-3.5 h-3.5" /> Copy brief</button>
        </div>
        <p className="text-xs text-gray-600 mt-1">Paste it into the builder chat. Once the changes are live, press &ldquo;Mark as built&rdquo; on each fix plan so tracking starts.</p>
      </div>
    )}
  </div>
);

export const FixQueueBar = ({ selected, onDone, onClear }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!job || job.status === 'done') return undefined;
    const t = setInterval(async () => {
      const res = await axios.get(`${API}/queue/${job.id}`, { headers: authHeaders() });
      setJob(res.data);
      if (res.data.status === 'done') onDone();
    }, 3000);
    return () => clearInterval(t);
  }, [job, onDone]);

  const start = async () => {
    setStarting(true);
    try {
      const res = await axios.post(`${API}/queue`, { items: selected }, { headers: authHeaders() });
      setJob(res.data);
      onClear();
      if (res.data.status === 'done') onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start the queue');
    } finally { setStarting(false); }
  };

  if (!selected.length && !job) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow p-4" data-testid="fix-queue-bar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-700"><b>{selected.length}</b> keyword{selected.length === 1 ? '' : 's'} ticked. Blog posts are applied straight away; main pages go into one build brief. Clashing fixes on the same page are skipped.</p>
        <div className="flex gap-2">
          {job && <button onClick={() => setJob(null)} className="text-xs text-gray-500 hover:text-gray-900" data-testid="fix-queue-dismiss">Dismiss</button>}
          <button onClick={start} disabled={!selected.length || starting || (job && job.status !== 'done')} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#A7468A] to-[#BC516F] text-white text-sm font-semibold disabled:opacity-50" data-testid="fix-queue-build">
            {starting || (job && job.status !== 'done') ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Hammer className="w-4 h-4" />} Build {selected.length || ''} selected
          </button>
        </div>
      </div>
      {job && <JobResult job={job} />}
    </div>
  );
};

export default FixQueueBar;
